import { Deferred, Head, usePoll } from "@inertiajs/react";
import { CheckCircle, Clock, FilePlus, FileText, RefreshCw, XCircle, Zap } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { ActivityFeed } from "@/components/activity-feed";
import { ActivityFeedSkeleton } from "@/components/activity-feed-skeleton";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useOrganizationRoutes } from "@/hooks/use-organization-routes";
import AppLayout from "@/layouts/app-layout";
import type { BreadcrumbItem, DailyActivityPageProps } from "@/types";

const POLL_INTERVAL_MS = 60000;

interface StatItem {
  label: string;
  value: number;
  icon: typeof FilePlus;
  tooltip: string;
}

interface StatSection {
  title: string;
  items: StatItem[];
}

export default function DailyActivity({
  metrics,
  activityFeed,
}: DailyActivityPageProps) {
  const routes = useOrganizationRoutes();
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Actividad del Día", href: routes.dailyActivity() },
  ];

  const [autoRefresh, setAutoRefresh] = useState(true);
  const { start, stop } = usePoll(POLL_INTERVAL_MS, {}, { autoStart: false });

  useEffect(() => {
    if (autoRefresh) {
      start();
    } else {
      stop();
    }

    return () => stop();
  }, [autoRefresh, start, stop]);

  const sections = useMemo<StatSection[]>(
    () => [
      {
        title: "Solicitudes",
        items: [
          {
            label: "Creadas",
            value: metrics.requests_created,
            icon: FilePlus,
            tooltip: "Solicitudes de compra creadas en las últimas 24 horas.",
          },
          {
            label: "Pendientes aprobación (actividad)",
            value: metrics.pending_approval,
            icon: Clock,
            tooltip:
              "Solicitudes con estado pendiente de aprobación que tuvieron actividad en las últimas 24 horas.",
          },
        ],
      },
      {
        title: "Aprobaciones",
        items: [
          {
            label: "Aprobadas (actividad)",
            value: metrics.approved_today,
            icon: CheckCircle,
            tooltip:
              "Solicitudes con estado aprobada que tuvieron actividad en las últimas 24 horas.",
          },
          {
            label: "Rechazadas (actividad)",
            value: metrics.rejected_today,
            icon: XCircle,
            tooltip:
              "Solicitudes con estado rechazada que tuvieron actividad en las últimas 24 horas.",
          },
        ],
      },
      {
        title: "Cotizaciones",
        items: [
          {
            label: "Subidas",
            value: metrics.quotes_uploaded,
            icon: FileText,
            tooltip:
              "Documentos de cotización cargados en las últimas 24 horas.",
          },
          {
            label: "Procesadas",
            value: metrics.quotes_processed,
            icon: FileText,
            tooltip:
              "Documentos de cotización procesados en las últimas 24 horas.",
          },
        ],
      },
      {
        title: "Solicitudes rápidas",
        items: [
          {
            label: "Creadas",
            value: metrics.quick_requests,
            icon: Zap,
            tooltip:
              "Solicitudes rápidas creadas en las últimas 24 horas (creación simple).",
          },
        ],
      },
    ],
    [metrics],
  );

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Actividad del Día" />

      <div className="flex h-full flex-1 flex-col gap-6 p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold">Actividad del Día</h1>
            <p className="text-muted-foreground">Últimas 24 horas</p>
          </div>
          <div className="flex items-center gap-3">
            <Switch
              id="auto-refresh"
              checked={autoRefresh}
              onCheckedChange={setAutoRefresh}
            />
            <label htmlFor="auto-refresh" className="text-sm">
              Auto-refresh
            </label>
            {autoRefresh && <RefreshCw className="h-4 w-4 animate-spin" />}
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Resumen 24 horas</CardTitle>
            <CardDescription>
              Indicadores clave de la actividad reciente
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
              {sections.map((section) => (
                <div key={section.title} className="space-y-3">
                  <div className="text-sm font-semibold text-foreground">
                    {section.title}
                  </div>
                  <div className="space-y-2">
                    {section.items.map((item) => (
                      <div
                        key={`${section.title}-${item.label}`}
                        className="flex items-center justify-between gap-4"
                      >
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <IconWithTooltip
                            icon={item.icon}
                            tooltip={item.tooltip}
                          />
                          <span>{item.label}</span>
                        </div>
                        <div className="text-lg font-semibold text-foreground">
                          {item.value}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Actividad reciente</CardTitle>
            <CardDescription>Últimos eventos de la organización</CardDescription>
          </CardHeader>
          <CardContent>
            <Deferred data="activityFeed" fallback={<ActivityFeedSkeleton />}>
              {activityFeed && <ActivityFeed activities={activityFeed} />}
            </Deferred>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}

function IconWithTooltip({
  icon: Icon,
  tooltip,
}: {
  icon: typeof FilePlus;
  tooltip: string;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-border bg-muted text-muted-foreground">
          <Icon className="h-4 w-4" />
        </span>
      </TooltipTrigger>
      <TooltipContent side="top" sideOffset={6}>
        {tooltip}
      </TooltipContent>
    </Tooltip>
  );
}
